import {
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    TextField,
    CircularProgress,
    Alert,
    IconButton,
    Box,
    FormControlLabel,
    Checkbox,
    Chip,
    InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation, TFunction } from 'react-i18next';
import CBFormTextFieldController from '../formController/CBFormTextFieldController';
import { useWatch } from 'react-hook-form';
import { IFormValues, OmooveCustomerResponse, OmooveModDataResponse } from '../../types';
import { useQuery } from '@tanstack/react-query';
import { getOmooveCustomer, getOmooveModData, getCarsharingClient } from '../../api/service/customerbaseApi';
import { CarsharingRedirectParams, redirectToCarsharing } from '../../../../util/carsharingService';
import i18n from '../../../../config/dictionary/i18n';
import { useEffect, useState } from 'react';
import { useCarSharingContext } from './CarSharingContext';

const ReadOnlyField = ({ label, value, InputProps }: { label: string, value: string | number | undefined | null, InputProps?: Record<string, any> }) => (
    <TextField
        label={label}
        value={value ?? '--'}
        fullWidth
        InputProps={{
            readOnly: true,
            ...InputProps,
        }}
    />
);

const languageLabels: Record<number, Record<string, string>> = {
    10002: { it: 'Italiano', en: 'Italian', fr: 'Italien', es: 'Italiano', pt:'Italiano'},
    10001: { it: 'Inglese', en: 'English', fr: 'Anglais', es: 'Inglés', pt: 'Inglês'},
    10004: { it: 'Francese', en: 'French', fr: 'Français', es: 'Francés', pt: 'Francês' },
};

const contractStateTranslationKeys: Record<number, string> = {
    0: 'csCustomerStatus.contract.state.lock',
    1: 'csCustomerStatus.contract.state.unlock',
    2: 'csCustomerStatus.contract.state.checked',
    3: 'csCustomerStatus.contract.state.canceled',
    4: 'csCustomerStatus.contract.state.expired',
};

interface PStateConfig {
    labelKey: string;
    variant: 'filled' | 'outlined';
    sx?: any;
}

const pStateConfig: Record<number, PStateConfig> = {
    0: { labelKey: 'csCustomerStatus.pState.blocked', variant: 'filled', sx: { backgroundColor: '#DC3545', color: 'white', fontWeight: 'bold' } },
    1: { labelKey: 'csCustomerStatus.pState.unlocked', variant: 'filled', sx: { backgroundColor: '#5DBE7B', color: 'white', fontWeight: 'bold' } },
    2: { labelKey: 'csCustomerStatus.pState.checked', variant: 'filled', sx: { backgroundColor: '#FFC107', color: 'white', fontWeight: 'bold' } },
    3: { labelKey: 'csCustomerStatus.pState.cancelled', variant: 'filled', sx: { backgroundColor: '#f55f6b', color: 'white', fontWeight: 'bold' } },
    4: { labelKey: 'csCustomerStatus.pState.new', variant: 'filled', sx: { backgroundColor: '#979797', color: 'white', fontWeight: 'bold' } },
};

function getContractStateLabel(contractStateID?: number | null, t?: TFunction): string {
    if (contractStateID == null) return '';
    const key = contractStateTranslationKeys[contractStateID];
    return key && t ? t(key) : '';
}

const cityMap: Record<number, string> = {
    1304: "Lione",
    404: "Milano",
    896: "Roma",
    1666: "Testcittamppre",
    41: "Torino",
};

function getCustomerStatusLabel(registrationStatus?: number | null): string {
    if (registrationStatus == null) return 'N/D';
    const statusNum = Number(registrationStatus) || 0;
    const mail = (statusNum & 1) > 0 ? 'OK' : 'NOK';
    const sms = (statusNum & 2) > 0 ? 'OK' : 'NOK';
    const docs = (statusNum & 4) > 0 ? 'OK' : 'NOK';
    const pay = (statusNum & 8) > 0 ? 'OK' : 'NOK';
    return `${mail} - ${sms} - ${docs} - ${pay}`;
}

function getProfileStatusLabel(registrationStatus?: number | null, t?: TFunction): string {
    if (registrationStatus == null) return 'N/D';

    const statusNum = Number(registrationStatus) || 0;
    const bits = statusNum & 15;

    const completeLabel = t?.('csCustomerStatus.profileStatuses.complete') ?? 'Complete';
    const firstAccessLabel = t?.('csCustomerStatus.profileStatuses.firstAccess') ?? 'First access';
    const docsValidatedLabel = t?.('csCustomerStatus.profileStatuses.docsValidated') ?? 'Doc validated';
    const incompleteLabel = t?.('csCustomerStatus.profileStatuses.partial') ?? 'Incomplete';

    switch (bits) {
        case 15: // OK - OK - OK - OK
            return completeLabel;
        case 7:  // OK - OK - OK - NOK
            return docsValidatedLabel;
        case 3:  // OK - OK - NOK - NOK
        case 1:  // OK - NOK - NOK - NOK
        case 11: // OK - OK - NOK - OK
            return incompleteLabel;
        case 0:  // NOK - NOK - NOK - NOK
            return firstAccessLabel;
        default:
            return '-';
    }
}

const checkboxes = [
    'General contract conditions',
    'Allow privacy policy',
    'Offers on LeasysGo products',
    'Target offers',
    'Offers on third parts product',
];

export default function CustomerBaseCarSharingStatus() {
    const { t } = useTranslation('customerbase');
    const { language, setPersonId, setContractId, setCsCustomerId, setMarket, setLanguage, setContractData, setIsModDataAvailable } = useCarSharingContext();
    const termOptions = t('csCustomerStatus.termsAndConditions.options', { returnObjects: true }) as Record<string, string>;
    const customerId = useWatch<IFormValues, 'cbDetails.cbCustomerId'>({ name: 'cbDetails.cbCustomerId' });
    const csCustomerId = useWatch<IFormValues, 'csData.csCustomerId'>({ name: 'csData.csCustomerId' });
    const idTenant = useWatch<IFormValues, 'cbDetails.idTenant'>({ name: 'cbDetails.idTenant' });
    const csDataValue = useWatch<IFormValues, 'csData'>({ name: 'csData' });
    const migratedAtValue = useWatch<IFormValues, 'csData.migratedAt'>({ name: 'csData.migratedAt' });
    const pinValue = useWatch<IFormValues, 'csData.pin'>({ name: 'csData.pin' });
    const billingCbCustomerIdValue = useWatch<IFormValues, 'csData.billingCbCustomerId'>({ name: 'csData.billingCbCustomerId' });

    const formatDateTime = (
        isoString?: string | null,
        locale = 'it-IT',
        options: Intl.DateTimeFormatOptions = {}
    ): string => {
        if (!isoString) return '';

        const date = new Date(isoString);
        if (Number.isNaN(date.getTime())) return '';

        const formatter = new Intl.DateTimeFormat(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            ...options,
        });


        const parts = Object.fromEntries(formatter.formatToParts(date).map(p => [p.type, p.value]));

        return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
    };


    const market = idTenant?.split('_')[1];
    const [expanded, setExpanded] = useState(true);

    const handleRedirect = async () => {
        if (!customerId) return;
        const params: CarsharingRedirectParams = {
            lang: i18n.language || 'it',
            type: 'CUSTOMER',
            section: 'REGISTRY',
            id: String(csCustomerId),
        };
        try {
            await redirectToCarsharing(params);
        } catch (error) {
            console.error('Redirect failed:', error);
        }
    };

    const {
        data: omooveCustomer,
        isError: isErrorCustomer,
        error: errorCustomer,
        status: omooveCustomerStatus,
    } = useQuery<OmooveCustomerResponse>(
        ['omooveCustomerData', csCustomerId, market],
        () => getOmooveCustomer(String(csCustomerId), undefined, market),
        { enabled: !!csCustomerId && !!market }
    );

    const {
        data: omooveData,
        isLoading: isLoadingMod,
        isError: isErrorMod,
        error: errorMod,
    } = useQuery<OmooveModDataResponse>(
        ['omooveModData', omooveCustomer?.body?.customer?.customerNumber, getCarsharingClient(), i18n.language],
        () =>
            getOmooveModData(
                String(omooveCustomer!.body.customer.customerNumber),
                getCarsharingClient(),
                i18n.language
            ),
        {
            enabled: omooveCustomerStatus === 'success' && !!omooveCustomer?.body?.customer?.customerNumber,
        }
    );

    useEffect(() => {
        const hasCustomerNumber = !!omooveCustomer?.body?.customer?.customerNumber;
        setIsModDataAvailable?.(hasCustomerNumber);


        if (!omooveCustomer) return;

        const personId = omooveCustomer.body.customer.customerNumber;
        if (personId) setPersonId?.(personId);

        if (omooveData) {
            setContractId?.(omooveData.contractData?.[0]?.contractID);
            setContractData?.(omooveData.contractData || []);
        }

        setCsCustomerId?.(String(csCustomerId));
        setMarket?.(market);
        setLanguage?.(i18n.language.toLowerCase());
    }, [omooveCustomer, omooveData, csCustomerId, market, i18n.language]);

    const isLoading =
        omooveCustomerStatus === 'loading' ||
        (omooveCustomerStatus === 'success' &&
            (!!omooveCustomer?.body?.customer?.customerNumber
                ? isLoadingMod && !isErrorMod
                : false));

    const isError = isErrorMod || isErrorCustomer;
    const error = errorMod || errorCustomer;

    const favoriteCityName = omooveCustomer
        ? cityMap[omooveCustomer?.body?.customer?.idSelectedCity ?? -1] ??
        String(omooveCustomer?.body?.customer?.idSelectedCity ?? '')
        : '';

    const mobilePhone = omooveCustomer?.body?.customer?.mobilePhone;
    const phonePrefix = mobilePhone?.startsWith('+') ? mobilePhone.substring(0, 3) : '';
    const phoneNumber = mobilePhone?.startsWith('+') ? mobilePhone.substring(3) : mobilePhone;
    const rulesStatus = omooveCustomer?.body?.customer?.rulesStatus ?? 0;

    return (
        <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ fontWeight: 500 }}>{t('tabs.accordionsLabels.csStatus')}</Typography>
                        {omooveData?.PState !== undefined && pStateConfig[omooveData.PState] && (
                            <Chip
                                label={t(pStateConfig[omooveData.PState].labelKey)}
                                color="default"
                                variant={pStateConfig[omooveData.PState].variant}
                                sx={pStateConfig[omooveData.PState].sx}
                                size="small"
                            />
                        )}
                    </Box>
                    {expanded && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                void handleRedirect();
                            }}
                            disabled={!customerId}
                            color="secondary"
                        >
                            <LinkIcon />
                        </IconButton>
                    )}
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <CBFormTextFieldController
                            formName="csData.csCustomerId"
                            readOnly
                            label={t('csCustomerStatus.csCustomerId')}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        {csDataValue?.modPersonId ?
                            (<CBFormTextFieldController formName="csData.modPersonId" readOnly label={t('csCustomerStatus.modPersonId')} />)
                            :
                            (<TextField label={t('csCustomerStatus.modPersonId')} value="--" fullWidth InputProps={{ readOnly: true }} />)
                        }
                    </Grid>

                    <Grid item xs={12} md={6}>
                        {csDataValue?.sku ?
                            (<CBFormTextFieldController formName="csData.sku" readOnly label={t('csCustomerStatus.sku')} />)
                            :
                            (<TextField label={t('csCustomerStatus.sku')} value="--" fullWidth InputProps={{ readOnly: true }} />)
                        }
                    </Grid>

                    {isLoading && (
                        <Grid item xs={12}>
                            <CircularProgress />
                        </Grid>
                    )}
                    {isError && (
                        <Grid item xs={12}>
                            <Alert severity="error">
                                {(error as any).response?.data?.errorMessage || (error as Error).message}
                            </Alert>
                        </Grid>
                    )}


                    {omooveCustomer?.body?.customer && (
                        <>
                            <Grid item xs={12} md={6}>
                                <ReadOnlyField
                                    label={t('csCustomerStatus.market')}
                                    value={omooveCustomer.body.customer.market}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ReadOnlyField
                                    label={t('csCustomerStatus.registrationStatus')}
                                    value={getCustomerStatusLabel(
                                        omooveCustomer.body.customer.registrationStatus
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ReadOnlyField
                                    label={t('csCustomerStatus.profileStatus')}
                                    value={getProfileStatusLabel(
                                        omooveCustomer.body.customer.registrationStatus,
                                        t
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ReadOnlyField
                                    label={t('csCustomerStatus.phonePrefix')}
                                    value={phonePrefix}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ReadOnlyField
                                    label={t('csCustomerStatus.phoneNumber')}
                                    value={phoneNumber}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ReadOnlyField
                                    label={t('csCustomerStatus.email')}
                                    value={omooveData?.mail ?? omooveCustomer.body.customer.email}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ReadOnlyField
                                    label={t('csCustomerStatus.favoriteCity')}
                                    value={favoriteCityName}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ReadOnlyField
                                    label={t('csCustomerStatus.preferredLanguage')}
                                    value={
                                        omooveData
                                            ? languageLabels[omooveData.languageID ?? 0]?.[i18n.language] ??
                                            ''
                                            : languageLabels[
                                            omooveCustomer.body.customer.idLanguage ?? 0
                                            ]?.[i18n.language] ?? ''
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ReadOnlyField
                                    label={t('csCustomerStatus.validationSmsCode')}
                                    value={omooveCustomer.body.customer.regSmsCode ?? '--'}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ReadOnlyField
                                    label={t('csCustomerStatus.promoCode')}
                                    value={omooveCustomer.body.customer.registrationCode ?? '--'}
                                />
                            </Grid>

                            {(pinValue !== null && pinValue !== undefined && pinValue !== '') && (
                                <Grid item xs={12} md={6}>
                                    <ReadOnlyField
                                        label={t('csCustomerStatus.pin')}
                                        value={pinValue}
                                    />
                                </Grid>
                            )}

                            {(migratedAtValue !== null && migratedAtValue !== undefined && migratedAtValue !== '') && (
                                <Grid item xs={12} md={6}>
                                    <ReadOnlyField
                                        label={t('csCustomerStatus.migratedAt')}
                                        value={formatDateTime(migratedAtValue)}
                                    />
                                </Grid>
                            )}

                            {(billingCbCustomerIdValue !== null && billingCbCustomerIdValue !== undefined && String(billingCbCustomerIdValue) !== '') && (
                                <Grid item xs={12} md={6}>
                                    <ReadOnlyField
                                        label={t('csCustomerStatus.billingCbCustomerId')}
                                        value={billingCbCustomerIdValue}
                                        InputProps={{
                                            endAdornment: (!!billingCbCustomerIdValue && String(billingCbCustomerIdValue) !== String(customerId)) ? (
                                                <InputAdornment position="end">
                                                    {/* Se billingCbCustomerId è presente e diverso da cbCustomerId,
                                                     mostra l'icona che apre /customerbase/view/${billingCbCustomerIdValue}
                                                     in una nuova scheda */}
                                                    <IconButton size="small" onClick={() => window.open(`/customerbase/view/${billingCbCustomerIdValue}`, '_blank')}>
                                                        <DescriptionIcon sx={{ color: '#C8017D' }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ mt: 2, mb: 1, fontWeight: 400, color: 'text.primary' }}
                                >
                                    {t('csCustomerStatus.termsAndConditions.title')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                {checkboxes.map((label, index) => {
                                    const isChecked = ((rulesStatus ?? 0) & (1 << index)) !== 0;
                                    return (
                                        <FormControlLabel
                                            key={index}
                                            control={
                                                <Checkbox
                                                    checked={isChecked}
                                                    disabled
                                                    sx={{
                                                        color: isChecked ? 'success.main' : 'text.disabled',
                                                        '&.Mui-checked': { color: 'success.main' },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    color="text.primary"
                                                    variant="body2"
                                                    sx={{ fontWeight: isChecked ? 500 : 400 }}
                                                >
                                                    {termOptions[label] || label}
                                                </Typography>
                                            }
                                        />
                                    );
                                })}
                            </Grid>


                            {omooveData?.contractData && omooveData.contractData.length > 0 && (
                                <>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ mt: 2 }}>
                                            {t('csCustomerStatus.contractDetails')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <ReadOnlyField
                                            label={t('csCustomerStatus.contractId')}
                                            value={omooveData.contractData[0].contractID}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <ReadOnlyField
                                            label={t('csCustomerStatus.contract.contractNumber')}
                                            value={omooveData.contractData[0].contractNumber}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <ReadOnlyField
                                            label={t('csCustomerStatus.contract.contractTypeName')}
                                            value={omooveData.contractData[0].contractTypeName}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <ReadOnlyField
                                            label={t('csCustomerStatus.contract.contractStateLabel')}
                                            value={getContractStateLabel(
                                                omooveData.contractData[0].contractStateID,
                                                t
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <ReadOnlyField
                                            label={t(
                                                'csCustomerStatus.contract.contractValidityEndDate'
                                            )}
                                            value={new Date(
                                                omooveData.contractData[0].contractDetailsData
                                                    .contractValidityEndDate
                                            ).toLocaleDateString()}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <ReadOnlyField
                                            label={t('csCustomerStatus.contract.category')}
                                            value={
                                                omooveData.contractData[0].isOwner
                                                    ? t('csCustomerStatus.contract.owner')
                                                    : t('csCustomerStatus.contract.guest')
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <ReadOnlyField
                                            label={t('csCustomerStatus.contract.enabledToService')}
                                            value={
                                                omooveData.contractData[0].amountActualBalance > 0
                                                    ? t('csCustomerStatus.contract.state.unlock')
                                                    : t('csCustomerStatus.contract.state.lock')
                                            }
                                        />
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}